import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'rsi',
  name: 'Relative Strength Index',
  shortName: 'RSI',
  category: 'momentum',
  overlay: false,
  params: [{ key: 'period', label: 'Period', type: 'number', default: 14, min: 2, max: 200 }],
  lines: [{ key: 'rsi', label: 'RSI', color: '#7b1fa2', width: 1 }],
  priceRange: { min: 0, max: 100 },
  priceLevels: [70, 30],
}

class RSI extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const period = Math.max(2, Number(params['period']) || 14)
    const values: (number | null)[] = new Array<number | null>(candles.length).fill(null)

    if (candles.length <= period) return { lines: [{ key: 'rsi', values }] }

    // Seed: simple average of first `period` gains and losses
    let avgGain = 0
    let avgLoss = 0
    for (let i = 1; i <= period; i++) {
      const delta = candles[i].c - candles[i - 1].c
      if (delta > 0) avgGain += delta
      else avgLoss += -delta
    }
    avgGain /= period
    avgLoss /= period

    const rs0 = avgLoss === 0 ? Infinity : avgGain / avgLoss
    values[period] = 100 - 100 / (1 + rs0)

    // Wilder's smoothing for subsequent values
    for (let i = period + 1; i < candles.length; i++) {
      const delta = candles[i].c - candles[i - 1].c
      const gain = delta > 0 ? delta : 0
      const loss = delta < 0 ? -delta : 0

      avgGain = (avgGain * (period - 1) + gain) / period
      avgLoss = (avgLoss * (period - 1) + loss) / period

      const rs = avgLoss === 0 ? Infinity : avgGain / avgLoss
      values[i] = 100 - 100 / (1 + rs)
    }

    return { lines: [{ key: 'rsi', values }] }
  }
}

registerIndicator(RSI, CONFIG)
